import React, { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ResultsToolbar from './ResultsToolbar';
import { useSelector } from 'react-redux';
import { IState } from 'appRedux/createStore';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';
import { OrderByEnum } from 'common/common';

export enum SnapPoint {
  Min = 'min',
  Mid = 'mid',
  Max = 'max',
  Full = 'full',
  Half = 'half',
  Quarter = 'quarter',
  // EGM Details open height: drawer covers 75% of the screen (top at 25%),
  // leaving a map band above for the selected pin.
  ThreeQuarters = 'threeQuarters',
  // "Hold open" — drawer top sits just below the Insight/Timeframe toolbar so
  // both the toolbar and the map remain interactive. Reached by drag; persists
  // across Insight/Timeframe changes and empty-filter auto-snaps.
  Hold = 'hold',
}

export type SnapPointType =
  | SnapPoint.Min
  | SnapPoint.Mid
  | SnapPoint.Max
  | SnapPoint.Full
  | SnapPoint.Half
  | SnapPoint.Quarter
  | SnapPoint.ThreeQuarters
  | SnapPoint.Hold;
const TAB_BAR_HEIGHT = 67;
const EXTRA_PADDING = 20;
const DETAIL_BOTTOM_SHEET = 230;
type ListSheetProps = {
  children: ReactNode;
  alwaysOpen?: boolean;
  initialSnap?: SnapPointType;
  minHeight?: number;
  midHeight?: number;
  maxHeight?: number;
  totalReportCount: number;
  groupGameName: string;
  onSnapChange?: (snap: SnapPointType) => void;
  isBankFilter: boolean;
  isShowResultToolBar?: boolean;
  showToolbarWhenEmpty?: boolean;
  onStatsRefChange?: HTMLDivElement | null;
  isLandscape: boolean;
  forceSnap?: SnapPointType | null;
  // Fit-to-content open (used by Compare): the natural pixel height of the
  // rendered content. When `fitContentSeq` changes, the drawer opens just far
  // enough to show that much content without an inner scroll, capped at Full
  // (100%) — it never opens past the top of the screen.
  fitContentHeight?: number;
  fitContentSeq?: number;
  insightKey: string;
  orderBy: OrderByEnum;
  timeframeKey: string;
  isFooterVisible?: boolean;
  onScrollContent?: (scrollTop: number, atBottom: boolean) => void;
  onTranslateYChange?: (panelTop: number) => void;
  // Fires true when the user starts dragging the drawer and false when the drag
  // ends, so the map can hide its recenter icon while the drawer is in motion.
  onDragStateChange?: (dragging: boolean) => void;
  onHeaderDoubleTap?: () => void;
  /** Insight + timeframe rows; title / handle / sticky head stay. Default true. */
  filtersVisible?: boolean;
  /**
   * When false, fade out the drawer header title + insight/timeframe rows
   * (EGM data loading). Opacity + pointer-events; not display:none. Default true.
   */
  headerChromeVisible?: boolean;
  /**
   * Loading collapse (display-only): slide the whole Insight + Timeframe header block
   * out while the EGM list loads and back in when it lands. Derived by the parent from
   * the loading skeleton; independent of `filtersVisible` (scroll auto-hide). While it
   * is true the toolbar stays mounted (collapsed) even with no rows yet, so the first
   * load slides it in instead of popping it in. Default false.
   */
  sectionsCollapsed?: boolean;
  // EGM action pills (Compare / Favorites / Playlist). When provided, they are
  // rendered floating just above the drawer (tracking its position live during a
  // drag), or — when the drawer is open far enough that there's no room above —
  // tucked between the drag handle and the header. Gated upstream by the
  // drawerActionButtons feature flag + selection state; ListSheet only positions.
  actionButtons?: ReactNode;
};

const ListSheet: React.FC<ListSheetProps> = ({
  children,
  alwaysOpen = false,
  initialSnap = SnapPoint.Mid,
  minHeight,
  midHeight,
  maxHeight,
  totalReportCount,
  groupGameName,
  onSnapChange,
  isBankFilter,
  isShowResultToolBar = true,
  showToolbarWhenEmpty = false,
  onStatsRefChange,
  isLandscape,
  forceSnap,
  fitContentHeight,
  fitContentSeq,
  insightKey,
  orderBy,
  timeframeKey,
  isFooterVisible = true,
  onScrollContent,
  onTranslateYChange,
  onDragStateChange,
  onHeaderDoubleTap,
  filtersVisible = true,
  headerChromeVisible = true,
  sectionsCollapsed = false,
  actionButtons,
}) => {
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [translateY, setTranslateY] = useState(0);
  const [insetBottom, setInsetBottom] = useState(0);
  const [insetTop, setInsetTop] = useState(0);

  const [startY, setStartY] = useState<number | null>(null);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  // Direction the next handle tap should move the drawer ('up' = toward Full,
  // 'down' = toward Min). Tapping the handle steps the drawer one snap stop at a
  // time and bounces at the extremes: keep going up until Full, then reverse and
  // go down until Min, then reverse again. Reset to null whenever the user drags
  // the drawer themselves so the cycle restarts from wherever they left it.
  const tapDirectionRef = useRef<'up' | 'down' | null>(null);
  // translateY captured when a drag begins, so touch/mouse end can tell that a
  // gesture started from the fully-collapsed position (a swipe up off the bottom).
  const dragStartYRef = useRef<number | null>(null);
  // Content max-height snapshot taken at drag start; held fixed while dragging so
  // the panel translates as a rigid block (children move in lockstep).
  const frozenMaxHeightRef = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [panelHeight, setPanelHeight] = useState<number>(0);
  // Distance from the floating panel's top edge to the scrollable .panel-content
  // top — i.e., the combined height of the drag handle, optional inline action
  // pills, and ResultsToolbar. Must be subtracted from panelContentMaxHeight or
  // the list scroll area extends past the tab bar and the last EGM row is
  // unreachable. Tracked in state (not just via ref) so the height calc
  // re-runs when pills wrap / toolbar height changes.
  const [contentOffsetTop, setContentOffsetTop] = useState<number>(0);
  // Measured height of the action-pills wrapper (it wraps to 1–2 rows depending
  // on how many pills show), used to position the floating variant just above
  // the drawer and to decide when there's no longer room above it.
  const actionButtonsRef = useRef<HTMLDivElement>(null);
  const [actionButtonsHeight, setActionButtonsHeight] = useState<number>(0);
  // Y position saved before filter panel expands so we can restore it after selection
  const preFilterYRef = useRef<number | null>(null);
  // Tracks the drawer's logical snap point so geometry changes (stats height after
  // a data refresh, resize, orientation) RE-PLACE the drawer at the same stop
  // instead of snapping it back to the initial position. Without this, the data
  // refresh that follows tapping a list shortcut (All/Favorites/Playlist) reopens a
  // drawer the user had closed.
  const currentSnapRef = useRef<SnapPointType>(alwaysOpen ? SnapPoint.Max : initialSnap);
  const hasInitializedRef = useRef(false);
  const isLandscapeOnly = useSelector<IState>(
    (state) => state.app.persistedState.orientationState?.isLandscape
  ) as boolean;
  const { activeSlotNumber } = useSlotDetailHistory();
  // The Insight/Timeframe toolbar. Stays mounted (collapsed) during a loading collapse
  // even before the first rows exist, so its height can animate instead of popping in.
  const showResultsToolbar =
    !isLandscapeOnly &&
    isShowResultToolBar &&
    (totalReportCount > 0 || showToolbarWhenEmpty || sectionsCollapsed);

  useEffect(() => {
    const setViewportHeight = () => {
      const height = window.innerHeight;
      const vh = height;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      setScreenHeight(window.innerHeight);
    };

    setViewportHeight();

    const computedStyle = getComputedStyle(document.documentElement);
    const inset = parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0', 10);
    setInsetBottom(inset);
    const topInset = parseInt(computedStyle.getPropertyValue('--safe-area-inset-top') || '0', 10);
    setInsetTop(topInset);

    if (contentRef.current) {
      setContentHeight(contentRef.current.offsetHeight);
    }

    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    window.addEventListener('focus', setViewportHeight);
    window.addEventListener('scroll', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
      window.removeEventListener('focus', setViewportHeight);
      window.removeEventListener('scroll', setViewportHeight);
    };
  }, []);

  const MIN_Y = useMemo(() => {
    if (isLandscapeOnly) {
      return minHeight ?? screenHeight + insetBottom - 30;
    }
    return minHeight ?? screenHeight - 90;
  }, [minHeight, screenHeight, isLandscapeOnly, insetBottom]);

  const MID_Y = useMemo(() => {
    if (activeSlotNumber) {
      return screenHeight - (DETAIL_BOTTOM_SHEET + TAB_BAR_HEIGHT + EXTRA_PADDING);
    }

    return midHeight ?? screenHeight * 0.5;
  }, [isShowResultToolBar, initialSnap, midHeight, screenHeight, onStatsRefChange?.offsetHeight]);

  const FULL_Y = useMemo(() => insetBottom, [insetBottom]);
  const QUARTER_Y = useMemo(() => screenHeight * 0.75, [screenHeight]);
  // Drawer top at 25% → sheet covers the bottom 75% (EGM Details default).
  const THREE_QUARTERS_Y = useMemo(() => screenHeight * 0.25, [screenHeight]);
  const HALF_Y = useMemo(() => screenHeight * 0.5, [screenHeight]);
  // Fixed-pixel drawer height (NOT a screen percentage) so the toolbar fits
  // identically on every device — a percentage gives 216px on a 800-px screen
  // but 243px on a 900-px screen, which is the inconsistency the user saw.
  // 230px is just enough room for: drag handle → header → Insight pills →
  // Timeframe pills, with no list rows peeking through.
  const HOLD_DRAWER_HEIGHT = 250;
  const HOLD_Y = useMemo(
    () => Math.max(0, screenHeight - HOLD_DRAWER_HEIGHT),
    [screenHeight]
  );

  const MAX_Y = useMemo(() => {
    if (initialSnap === SnapPoint.Full) return FULL_Y;
    return maxHeight ?? screenHeight * 0.2;
  }, [maxHeight, screenHeight, initialSnap, FULL_Y]);

  useLayoutEffect(() => {
    if (panelRef.current) {
      setPanelHeight(panelRef.current.offsetHeight);
    }
  }, [isShowResultToolBar]);

  // Publish drawer geometry as CSS variables so descendants (sticky headers)
  // can paint a gradient that aligns with the drawer's own gradient at the
  // same Y. Without these, sticky headers paint a compressed copy of the
  // gradient that visually mismatches the drawer.
  //
  // IMPORTANT: do NOT observe `.panel-content` here. Its maxHeight is derived
  // from contentOffsetTop — watching it while also writing that maxHeight
  // (and while the insight/timeframe collapse animates toolbar height) feeds a
  // ResizeObserver loop ("loop completed with undelivered notifications").
  // Observe the panel + header chrome only, defer work to rAF, and skip
  // setState when the measured offset hasn't changed.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const content = contentRef.current;
    if (!panel) return;
    let rafId = 0;
    const updateGeometryVars = () => {
      const panelH = panel.offsetHeight;
      const contentTop = content ? content.offsetTop : 0;
      panel.style.setProperty('--ls-height', `${panelH}px`);
      panel.style.setProperty('--ls-content-top', `${contentTop}px`);
      setContentOffsetTop((prev) => (prev === contentTop ? prev : contentTop));
    };
    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updateGeometryVars();
      });
    };
    updateGeometryVars();
    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(panel);
    // Header band above the scroller (handle / toolbar / inline actions) —
    // these are what move content.offsetTop when filters collapse.
    panel.querySelectorAll('.handle, .results-toolbar, .drawer-action-buttons').forEach((el) => {
      ro.observe(el);
    });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [isShowResultToolBar, isLandscape, activeSlotNumber, filtersVisible, showResultsToolbar]);

  // Keep the action-pills height current (it changes as pills wrap / toggle), so
  // the floating placement sits a consistent 5px above the drawer and the
  // "no room above" check stays accurate.
  useLayoutEffect(() => {
    const el = actionButtonsRef.current;
    if (!el) {
      setActionButtonsHeight(0);
      return;
    }
    let rafId = 0;
    const update = () => {
      const h = el.offsetHeight;
      setActionButtonsHeight((prev) => (prev === h ? prev : h));
    };
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };
    update();
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [actionButtons]);

  const yForSnap = (snap: SnapPointType) => {
    switch (snap) {
      case SnapPoint.Min:
        return MIN_Y;
      case SnapPoint.Max:
        return MAX_Y;
      case SnapPoint.Full:
        return FULL_Y;
      case SnapPoint.Half:
        return HALF_Y;
      case SnapPoint.Quarter:
        return QUARTER_Y;
      case SnapPoint.ThreeQuarters:
        return THREE_QUARTERS_Y;
      case SnapPoint.Hold:
        return HOLD_Y;
      default:
        return MID_Y;
    }
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      if (!hasInitializedRef.current) {
        // First mount: place the drawer at its initial snap and announce it.
        const snap: SnapPointType = alwaysOpen ? SnapPoint.Max : initialSnap;
        currentSnapRef.current = snap;
        setTranslateY(yForSnap(snap));
        onSnapChange?.(snap);
        hasInitializedRef.current = true;
        return;
      }
      // Geometry changed (stats height after a data refresh, resize, orientation).
      // Re-place the drawer at its CURRENT snap so it tracks the new layout without
      // reopening a drawer the user has moved or closed.
      setTranslateY(yForSnap(currentSnapRef.current));
    });
  }, [alwaysOpen, MAX_Y, MID_Y, MIN_Y, FULL_Y, HALF_Y, QUARTER_Y, THREE_QUARTERS_Y]);

  useEffect(() => {
    if (!forceSnap) return;
    currentSnapRef.current = forceSnap;
    setTransitionEnabled(true);
    switch (forceSnap) {
      case SnapPoint.Full:
        setTranslateY(FULL_Y);
        break;
      case SnapPoint.Max:
        setTranslateY(MAX_Y);
        break;
      case SnapPoint.Mid:
        setTranslateY(MID_Y);
        break;
      case SnapPoint.Min:
        setTranslateY(MIN_Y);
        break;
      case SnapPoint.Half:
        setTranslateY(HALF_Y);
        break;
      case SnapPoint.Quarter:
        setTranslateY(QUARTER_Y);
        break;
      case SnapPoint.ThreeQuarters:
        setTranslateY(THREE_QUARTERS_Y);
        break;
      case SnapPoint.Hold:
        setTranslateY(HOLD_Y);
        break;
    }
    onSnapChange?.(forceSnap);
  }, [forceSnap]);

  // One-shot "fit to content" open. Positions the drawer top so the scrollable
  // content area exactly holds `fitContentHeight` px of content (no inner
  // scroll), inverting computePanelContentMaxHeight. Clamped between Full (can't
  // open past the top → the 100% cap) and Min (fully collapsed) so an
  // over-tall selection simply lands at Full and scrolls. Fires only when the
  // seq changes, so a later content resize or a user drag isn't yanked back.
  useEffect(() => {
    if (!fitContentSeq) return;
    if (!fitContentHeight || fitContentHeight <= 0) return;
    const safeBottom = Math.max(0, insetBottom);
    const footerOffset = isFooterVisible ? TAB_BAR_HEIGHT + safeBottom : safeBottom;
    const bottomInset = isLandscape ? safeBottom : footerOffset;
    const rawY =
      screenHeight + insetBottom - bottomInset - contentOffsetTop - fitContentHeight;
    const targetY = Math.max(FULL_Y, Math.min(rawY, MIN_Y));
    // Track the fit locally (so a resize re-places THIS drawer sensibly) but do
    // NOT call onSnapChange: that snap is shared with the parent's `currentSnap`,
    // which seeds the Explore drawer's initialSnap. Leaking Full here would make
    // the Explore drawer reopen fully expanded when the user exits compare,
    // instead of returning to its default position.
    currentSnapRef.current = SnapPoint.Full;
    setTransitionEnabled(true);
    setTranslateY(targetY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitContentSeq]);

  useLayoutEffect(() => {
    document.body.getBoundingClientRect();
  }, []);


  // Notify parent of the current panel top position so the map can resize.
  // Only fire after dragging ends (not during active drag) to prevent the map
  // from panning continuously while the drawer is being moved.
  useEffect(() => {
    if (isDragging) return;
    if (onTranslateYChange) {
      onTranslateYChange(translateY - insetBottom);
    }
  }, [translateY, insetBottom, isDragging]);

  // Let the parent (and the map) know when the drawer drag starts/stops.
  useEffect(() => {
    onDragStateChange?.(isDragging);
  }, [isDragging]);

  // Content space from the panel top down to the footer/safe-area edge, for a
  // given drawer position. Defined here (not just in render) so the drag-start
  // handlers can snapshot it to freeze the content height during a drag.
  const computePanelContentMaxHeight = (ty: number) => {
    const top = ty - insetBottom;
    const safeBottom = Math.max(0, insetBottom);
    const footerOffset = isFooterVisible ? TAB_BAR_HEIGHT + safeBottom : safeBottom;
    // Subtract the handle + toolbar stack above .panel-content; otherwise at
    // Full snap the scroll area extends past the tab bar and the last row of a
    // list-that-fits is unreachable.
    const portrait = screenHeight - top - footerOffset - contentOffsetTop;
    const landscape = screenHeight - top - safeBottom - contentOffsetTop;
    return isLandscape ? Math.max(0, landscape) : Math.max(0, portrait);
  };

  // The drawer settles onto one of four stops: bottom (Min), 50% (Half), Hold
  // (~35% from top, just below the Insight/Timeframe toolbar), or fully open
  // (Full). The candidates are restricted to stops PAST the drag-start position
  // in the drag direction (drag up → only stops above start; drag down → only
  // stops below). Among those, the one the user released closest to wins. This
  // means:
  //   • a small drag up from Mid/Half lands on Hold — Half can't pull it back
  //     by proximity because Half isn't a candidate (drag started there);
  //   • a drag down from Full that overshoots Hold by a hair still lands on
  //     Hold — it's the nearest in-direction candidate, not Half;
  //   • a longer drag past one stop into another's natural zone snaps to the
  //     stop the user actually released near.
  // A movement too small to be a swipe is treated as a tap (handle's onClick
  // handles taps) and leaves the drawer where it was.
  const SNAP_MOVE_THRESHOLD = 10;
  const settleDrawerDrag = () => {
    const dragStartY = dragStartYRef.current;
    dragStartYRef.current = null;
    if (dragStartY === null) return;

    const moved = translateY - dragStartY;
    if (Math.abs(moved) < SNAP_MOVE_THRESHOLD) {
      // Treat as a tap — leave position to the onClick handler.
      setTranslateY(dragStartY);
      setStartY(null);
      return;
    }
    const draggedUp = moved < 0;

    const stops: Array<[number, SnapPoint]> = [
      [FULL_Y, SnapPoint.Full],
      [HOLD_Y, SnapPoint.Hold],
      [HALF_Y, SnapPoint.Half],
      [MIN_Y, SnapPoint.Min],
    ];
    const candidates = stops.filter(([y]) =>
      draggedUp ? y < dragStartY : y > dragStartY
    );
    if (candidates.length === 0) {
      // Already at the extreme stop in the drag direction — restore position.
      setTranslateY(dragStartY);
      setStartY(null);
      return;
    }

    let best = candidates[0];
    let bestDist = Math.abs(translateY - best[0]);
    for (const c of candidates) {
      const d = Math.abs(translateY - c[0]);
      if (d < bestDist) {
        bestDist = d;
        best = c;
      }
    }
    const [target, snap] = best;

    setTranslateY(target);
    setStartY(null);
    currentSnapRef.current = snap;
    // The user moved the drawer by hand — restart the tap cycle so the next
    // handle tap is judged fresh from this new resting position.
    tapDirectionRef.current = null;
    onSnapChange?.(snap);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (alwaysOpen) return;
    setStartY(e.clientY);
    dragStartYRef.current = translateY;
    frozenMaxHeightRef.current = computePanelContentMaxHeight(translateY);
    setTransitionEnabled(false);
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (alwaysOpen || startY === null || !isDragging) return;
    const delta = e.clientY - startY;
    const newY = Math.max(FULL_Y, Math.min(MIN_Y, translateY + delta));
    setTranslateY(newY);
    setStartY(e.clientY);
  };

  const handleMouseUp = () => {
    if (alwaysOpen || !isDragging) return;
    setTransitionEnabled(true);
    setIsDragging(false);
    frozenMaxHeightRef.current = null;
    settleDrawerDrag();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMoveWrapper = (e: MouseEvent) => handleMouseMove(e);
    const handleMouseUpWrapper = () => handleMouseUp();

    window.addEventListener('mousemove', handleMouseMoveWrapper);
    window.addEventListener('mouseup', handleMouseUpWrapper);

    return () => {
      window.removeEventListener('mousemove', handleMouseMoveWrapper);
      window.removeEventListener('mouseup', handleMouseUpWrapper);
    };
  }, [isDragging, startY, translateY]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (alwaysOpen) return;
    setStartY(e.touches[0].clientY);
    dragStartYRef.current = translateY;
    frozenMaxHeightRef.current = computePanelContentMaxHeight(translateY);
    setTransitionEnabled(false);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (alwaysOpen || startY === null) return;
    const delta = e.touches[0].clientY - startY;
    const newY = Math.max(FULL_Y, Math.min(MIN_Y, translateY + delta));
    setTranslateY(newY);
    setStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (alwaysOpen) return;
    setTransitionEnabled(true);
    setIsDragging(false);
    frozenMaxHeightRef.current = null;
    settleDrawerDrag();
  };

  // Tapping the handle walks the drawer one snap stop at a time. The stops are
  // the same ones a drag settles onto, ordered top-to-bottom (Full → … → Min).
  // From any stop below the top, taps move the drawer UP until it reaches Full;
  // once at Full, taps move it DOWN until Min; at Min it reverses again. A manual
  // drag clears tapDirectionRef so the next tap starts fresh from the new spot:
  // moving up unless already at the top, in which case it moves down.
  const handleHandleTap = () => {
    if (alwaysOpen) return;

    const stops: Array<[number, SnapPoint]> = [
      [FULL_Y, SnapPoint.Full],
      [HOLD_Y, SnapPoint.Hold],
      [HALF_Y, SnapPoint.Half],
      [MIN_Y, SnapPoint.Min],
    ];
    stops.sort((a, b) => a[0] - b[0]);

    // Locate the stop the drawer is currently resting nearest to.
    let currentIndex = 0;
    let bestDist = Math.abs(translateY - stops[0][0]);
    for (let i = 1; i < stops.length; i += 1) {
      const d = Math.abs(translateY - stops[i][0]);
      if (d < bestDist) {
        bestDist = d;
        currentIndex = i;
      }
    }

    const topIndex = 0;
    const bottomIndex = stops.length - 1;

    // Decide which way to step. The extremes force a reversal; otherwise keep
    // going the way the cycle was already headed (default up on a fresh start).
    let direction: 'up' | 'down';
    if (currentIndex <= topIndex) {
      direction = 'down';
    } else if (currentIndex >= bottomIndex) {
      direction = 'up';
    } else {
      direction = tapDirectionRef.current ?? 'up';
    }
    tapDirectionRef.current = direction;

    const nextIndex =
      direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const [target, snap] = stops[Math.max(topIndex, Math.min(bottomIndex, nextIndex))];

    setTranslateY(target);
    setTransitionEnabled(true);
    currentSnapRef.current = snap;
    onSnapChange?.(snap);
  };

  // While dragging, hold the content height fixed (snapshot taken at drag start)
  // so the panel moves as one rigid block — its children translate in lockstep
  // with the drawer instead of reflowing/clipping as the live height recomputes.
  const panelContentMaxHeight =
    isDragging && frozenMaxHeightRef.current !== null
      ? frozenMaxHeightRef.current
      : computePanelContentMaxHeight(translateY);

  // Action-pill placement. `panelTop` is the panel's top in viewport px (it is the
  // value used for the panel transform below). When the drawer is open far enough
  // that the pills + 5px gap + top safe-area no longer fit above it, fall back to
  // the inline placement (between handle and header). This is a continuous check
  // (not keyed on the snap point) so it stays correct mid-drag.
  const ACTION_BUTTONS_GAP = 5;
  const panelTop = translateY - insetBottom;
  const showActionButtonsFloating =
    !!actionButtons && panelTop >= actionButtonsHeight + ACTION_BUTTONS_GAP + insetTop;
  const showActionButtonsInline = !!actionButtons && !showActionButtonsFloating;

  return (
    <div className="list-bottom-parent">
      {showActionButtonsFloating && (
        <div
          ref={actionButtonsRef}
          className="drawer-action-buttons drawer-action-buttons--floating"
          style={{
            // Sit ACTION_BUTTONS_GAP px above the drawer's top edge, tracking it
            // live (re-rendered on every translateY change, including drags).
            top: `${panelTop - actionButtonsHeight - ACTION_BUTTONS_GAP}px`,
          }}
        >
          {actionButtons}
        </div>
      )}
      <div
        className="floating-panel"
        ref={panelRef}
        style={{
          transform: `translateY(${translateY - insetBottom}px)`,
          transition: transitionEnabled ? 'transform 0.25s ease' : 'none',
          // Figma Explore Drawer (12165:64535): 40px top corners.
          borderRadius: alwaysOpen ? '0' : '40px 40px 0 0',
          backgroundColor: '#e0e0e0',
        }}
      >
        {!alwaysOpen && (
          <div
            className="handle"
            onClick={handleHandleTap}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            onMouseDown={handleMouseDown}
          >
            <div className="drag-bar" />
          </div>
        )}
        {showActionButtonsInline && (
          <div
            ref={actionButtonsRef}
            className="drawer-action-buttons drawer-action-buttons--inline"
          >
            {actionButtons}
          </div>
        )}
        {showResultsToolbar && (
          <ResultsToolbar
            totalReportCount={totalReportCount}
            gameGroupName={groupGameName}
            isBankFilter={isBankFilter}
            insightKey={insightKey}
            orderBy={orderBy}
            timeframeKey={timeframeKey}
            filtersVisible={filtersVisible}
            headerChromeVisible={headerChromeVisible}
            sectionsCollapsed={sectionsCollapsed}
            onHeaderDoubleTap={onHeaderDoubleTap}
            onFiltersToggle={(open) => {
              if (open) {
                const isAlreadyFull = Math.abs(translateY - FULL_Y) < 2;
                if (!isAlreadyFull) {
                  preFilterYRef.current = translateY;
                  setTranslateY(MAX_Y);
                  setTransitionEnabled(true);
                }
              } else {
                if (preFilterYRef.current !== null) {
                  setTranslateY(preFilterYRef.current);
                  setTransitionEnabled(true);
                  preFilterYRef.current = null;
                }
              }
            }}
          />
        )}
        <div
          className="panel-content"
          ref={contentRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            if (onScrollContent) {
              // Clamp to the real scroll range so iOS overscroll "bounce"
              // beyond the top/bottom doesn't report values outside
              // [0, maxScroll] (which makes the footer flicker on the bounce).
              const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
              const clampedScrollTop = Math.min(Math.max(0, el.scrollTop), maxScroll);
              // Wider near-bottom band (~100px) so the footer stays pinned and
              // filters freeze before the exact end stop — avoids a ±tab-bar
              // maxHeight jump under a bottom-pinned scrollTop. Non-scrollable
              // panels (maxScroll === 0) are never "at bottom".
              const NEAR_BOTTOM_PX = 100;
              const atBottom =
                maxScroll > 0 && clampedScrollTop >= maxScroll - NEAR_BOTTOM_PX;
              onScrollContent(clampedScrollTop, atBottom);
            }
          }}
          style={{
            maxHeight: `${panelContentMaxHeight}px`,
            overflowY: 'auto',
            overflowX: 'hidden',
            // `none` (not `contain`) so pulling past the top doesn't rubber-band
            // and open a gap above the first row.
            overscrollBehaviorY: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default ListSheet;
